// eslint-disable-next-line monorepo-dependencies/no-unlisted-package-import
import type { CastReceiverContext, PlayerManager } from 'chromecast-caf-receiver/cast.framework';
import cast from '../../types/chromecast-caf-receiver/index';
import store, { SUBTITLES } from '../../store';
import { makeMediaObject } from '../../services/media';
import {
  bootstrapAction,
  editTracksInfoFilter,
  mediaFilter,
  playbackConfigFilter,
} from '../extensions';

export class CaptionsPlugin {
  private inBreak = false;
  private textTracks: ({ __typename: 'Subtitle'; useAsDefault: boolean | null } | null)[];
  init() {
    bootstrapAction.register(this.bootstrap.bind(this));
    mediaFilter.register(this.mediaTransform.bind(this));
    playbackConfigFilter.register(async playbackConfig => {
      Object.assign(playbackConfig, {
        shakaConfig: {
          streaming: {
            ignoreTextStreamFailures: true,
          },
        },
      });
      return playbackConfig;
    });
  }

  async mediaTransform(media: ReturnType<typeof makeMediaObject>, accessToken: string) {
    this.textTracks = media.texttracks;
    return media;
  }

  async bootstrap(context: CastReceiverContext) {
    const playerManager = context.getPlayerManager();

    this.keepTrackOfAdBreaks(playerManager);
    this.handleDefaultCaptions(playerManager);
    this.handleTogglingUsingRemote(playerManager);
  }

  private keepTrackOfAdBreaks(playerManager: PlayerManager) {
    playerManager.addEventListener(cast.framework.events.EventType.BREAK_STARTED, () => {
      this.inBreak = true;
    });
    playerManager.addEventListener(cast.framework.events.EventType.BREAK_ENDED, () => {
      this.inBreak = false;
    });
  }

  private handleDefaultCaptions(playerManager: PlayerManager) {
    playerManager.addEventListener(cast.framework.events.EventType.LOADED_DATA, s => {
      if (!this.inBreak) {
        const textTracksManager = playerManager.getTextTracksManager();
        const { TextTrackStyle } = cast.framework.messages;

        const textTrackStyling = Object.assign(new TextTrackStyle(), {
          fontFamily: 'Arial',
          backgroundColor: '#00000080',
        });
        textTracksManager.setTextTrackStyle(textTrackStyling);

        const tracks = textTracksManager.getTracks();
        const defaultIndex = this.textTracks.findIndex(track => track?.useAsDefault);

        const openCaption = tracks.find(track => track.subtype === 'SUBTITLE');
        const closedCaption = tracks.find(track => track.subtype === 'CAPTION');

        if (defaultIndex >= 0 && tracks?.[defaultIndex]) {
          playerManager.getTextTracksManager().setActiveByIds([tracks?.[defaultIndex]?.trackId]);
        } else if (closedCaption?.trackId && openCaption?.trackId) {
          playerManager.getTextTracksManager().setActiveByIds([openCaption?.trackId]);
        }

        store.set(SUBTITLES, { openCaption, closedCaption });

        const mediaInformation = playerManager.getMediaInformation();
        if (mediaInformation) {
          Object.assign(mediaInformation, {
            customData: {
              tracks: {
                openCaption: { trackId: openCaption?.trackId },
                closedCaption: { trackId: closedCaption?.trackId },
              },
            },
          });
          playerManager.setMediaInformation(mediaInformation);
        }
      }
    });
  }

  private handleTogglingUsingRemote(playerManager: PlayerManager) {
    editTracksInfoFilter.register(async request => {
      if (request.requestId === 0) {
        const activeTrack = playerManager.getTextTracksManager().getActiveTracks()[0];
        if (activeTrack) {
          const currentSubtitles = store.get(SUBTITLES);
          if (currentSubtitles?.openCaption?.trackId && currentSubtitles?.closedCaption?.trackId) {
            if (activeTrack.subtype === 'SUBTITLE') {
              request.activeTrackIds = [currentSubtitles?.closedCaption?.trackId];
            } else if (activeTrack.subtype === 'CAPTION') {
              request.activeTrackIds = [currentSubtitles?.openCaption?.trackId];
            }
          }
        }
      }
      return request;
    });
  }
}
